import React, { useState } from "react";
import { CustomButton } from "@/components/ui/Buttons";
import { Pencil } from "lucide-react";
import Modal from "@/components/ui/Modal";
import EditProfessionalDetailsModal from "./modals/EditProfessionalDetailsModal";
import { useAppSelector } from "@/redux/hooks/hooks";
import { TUser } from "@/types/types";
import { isEmptyValue } from "./CreatorExtrasDetails";

// Types
type SelectOption = {
  id: string;
  title: string;
};
export const humanizeKey = (k: string) =>
  k
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
export const experienceLevel: SelectOption[] = [
  { id: "beginner", title: "Beginner" },
  { id: "amateur", title: "Amateur" },
  { id: "professional", title: "Professional" },
  { id: "seasoned", title: "Seasoned" },
  { id: "star", title: "Star/Lead Role" },
];

const ProfileProfessionalDetails = ({ data }: { data: TUser | undefined }) => {
  const [isOpen, setIsOpen] = useState(false);
  // console.log(data);

  const DetailField = ({
    label,
    value,
  }: {
    label: string;
    value: string | boolean | undefined;
  }) => (
    <div>
      <h5 className="text-sm font-medium text-gray-900 mb-1">{label}</h5>
      <p
        className={`text-sm truncate w-[120px] lg:w-full ${
          value === null || value === undefined
            ? "text-red-500 font-medium"
            : "text-gray-600"
        }`}
      >
        {typeof value === "boolean"
          ? value
            ? "Yes"
            : "No"
          : value || "Not provided"}
      </p>
    </div>
  );

  const LanguagesList = ({ items }: { items: any[] }) => {
    const list = (items || []).filter((x) => !isEmptyValue(x?.name));
    if (list.length === 0) return <span className="text-[#98A2B3]">—</span>;

    return (
      <div className="flex flex-wrap gap-2">
        {list.map((l: any) => (
          <span
            key={l?.id || l?.name}
            className="inline-flex items-center gap-2 text-xs px-3 py-1 rounded-full border border-[#E5E9EB] bg-[#F7F7F7] text-[#475467]"
          >
            <span className="font-medium">{l.name}</span>
            {l?.fluency ? (
              <span className="px-2 py-0.5 rounded-full border border-[#E4E7EC] bg-white text-[#344054]">
                {humanizeKey(String(l.fluency))}
              </span>
            ) : null}
          </span>
        ))}
      </div>
    );
  };
  return (
    <>
      <div className="bg-white p-6 border border-[#E4E7EC] rounded-xl shadow space-y-5">
        <div className="flex justify-between items-center">
          <h6 className="text-[#344054] text-sm leading-5 font-medium">
            Professional Profile
          </h6>
          <CustomButton
            onClick={() => setIsOpen(true)}
            variant="outline"
            color="#344054"
            fontSize="14px"
            padding="8px 12px"
            border="1px solid #E8EBEE"
            borderRadius="8px"
            className="flex items-center gap-1"
          >
            <Pencil className="size-4" />
            Edit
          </CustomButton>
        </div>

        <div className="grid grid-cols-2 place-content-between gap-y-5">
          <DetailField label="Available skills" value={data?.skills} />
          <DetailField
            label="Topics are you comfortable speaking about"
            value={data?.topics}
          />
          <DetailField label="Hours per Week" value={data?.hoursPerWeek} />
          <DetailField
            label="Weekend Availability"
            value={data?.availableInWeekends}
          />
          {data?.salary && (
            <DetailField label="Last Drawn Salary" value={data?.salary} />
          )}
        </div>

        {/* <div>
          <h5 className="text-sm font-medium text-gray-900 mb-1">
            Previous Video Links
          </h5>
          {data?.videoLinks?.length === 0 ? (
            <>
              <p className="text-sm text-red-500 font-medium">
                No Links Provided
              </p>
            </>
          ) : (
            <>
              {data?.videoLinks.map((link, i) => (
                <a href={link} key={i} target="_blank">
                  <p className="text-sm text-blue-500  underline ">{link}</p>
                </a>
              ))}
            </>
          )}
        </div> */}
        {/* <div>
          <h5 className="text-sm font-medium text-gray-900 mb-1">
            Brands Not Comfortable Working With
          </h5>
          <p
            className={`text-sm  ${
              data?.brandsDontWantToWorkWith ? "text-gray-600" : "text-red-500"
            } `}
          >
            {data?.brandsDontWantToWorkWith || "Not provided"}
          </p>
        </div> */}

        {data?.payPerVideo && (
          <div>
            <h5 className="text-sm font-medium text-gray-900 mb-1">
              Expected Pay per Video
            </h5>
            <p className="text-sm text-gray-600">
              {data?.payPerVideo || "Not provided"}
            </p>
          </div>
        )}

        {/* <div>
          <p className="text-sm text-gray-600">
          {data?.language || "Not provided"}
          </p>
          </div> */}

        <h5 className="text-sm font-medium text-gray-900 mb-1">Languages</h5>
        <LanguagesList items={data?.languagesWithFluency || []} />
        {/* </Section> */}
      </div>

      <Modal
        preventCloseOnClickOutside={true}
        width="555px"
        isOpen={isOpen}
        setOpen={setIsOpen}
        padding="0px"
      >
        <EditProfessionalDetailsModal
          data={data as TUser}
          onClose={setIsOpen}
        />
      </Modal>
    </>
  );
};

export default ProfileProfessionalDetails;
